import { useCallback } from 'react';
import { useShowContext } from 'framework/show';
import useStaffingRiskService from '../../services/staffingRiskService';
import { StaffingDataType, StaffingRisk } from 'domain/shared/staffing';

interface FollowUpFormData {
  description: string;
  followUpDate: string;
}

export const useDashboardCallbacks = (refetch?: () => Promise<void>) => {
  const { data, setData } = useShowContext<StaffingDataType>();
  const { submitIssues, createFollowUp } = useStaffingRiskService();

  const projectData = data?.getData();

  const updateProjectData = useCallback(
    (updates: Partial<StaffingDataType>) => {
      if (setData) {
        setData({
          ...projectData,
          ...updates,
        });
      }
    },
    [projectData, setData],
  );

  const handleIssuesSubmit = useCallback(
    async (formData: { id: string; issues: any[] }) => {
      try {
        const submitData = {
          id: formData.id,
          issues: formData.issues || [],
        };

        const staffingRiskId = projectData?.staffingRisk?.id;
        if (!staffingRiskId) {
          throw new Error('Staffing risk ID not found');
        }

        const result = await submitIssues(staffingRiskId, submitData);

        if (projectData.staffingRisk) {
          updateProjectData({
            staffingRisk: {
              ...projectData.staffingRisk,
              issues: result.issues || [],
              priority: result.priority || projectData.staffingRisk.priority,
            } as StaffingRisk,
            currentZone: result.priority || projectData.currentZone,
          });
        }
        await refetch?.();
        return result;
      } catch (error) {
        updateProjectData(projectData);
        throw error;
      }
    },
    [projectData, submitIssues, updateProjectData, refetch],
  );

  const handleFollowUpSubmit = useCallback(
    async (formData: FollowUpFormData) => {
      const staffingRiskId = projectData?.staffingRisk!.id;

      const result = await createFollowUp(staffingRiskId!, formData);

      if (projectData.staffingRisk) {
        updateProjectData({
          staffingRisk: {
            ...projectData.staffingRisk,
            followUps: [...projectData.staffingRisk.followUps, result],
          } as StaffingRisk,
        });
      }
      await refetch?.();
      return result;
    },
    [projectData, createFollowUp, updateProjectData, refetch],
  );

  return {
    projectData,
    handleIssuesSubmit,
    handleFollowUpSubmit,
    updateProjectData,
  };
};
